import { LoginService } from '../../shared-modules/login/login.service';
import { ErrorList } from '../controls/error-prompt/error-list.model';
import { Attachment } from '../models/attachment.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Component, Inject, OnInit, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { Lookup } from '../lookup/lookup.model';
import { NgForm } from '@angular/forms';
import { System } from '../System';
import { SecurityService } from '../security/security.service';

/**
 * This dialog can be used whenever you need to upload an attachment.
 *
 * MAT_DIALOG_DATA:
 *      attachment: an attachment that the file will be attached to
 *      newAttachment: a boolean indicating whether this is adding or editing
 *
 * After saving, the attachment will have a file base 64 string in MyFileBase64String
 */
@Component({
    selector: 'add-attachment',
    templateUrl: './add-attachment-dialog.component.html',
    styleUrls: [
        './add-attachment-dialog.component.css'
    ]
})
export class AddAttachmentDialogComponent implements OnInit {

    // Static Properties
    static DefaultAllowedFileTypes: string[] = [
        'pdf',
        'jpg',
        'jpeg',
        'png',
        'gif',
        'txt',
        'doc',
        'docx',
        'xls',
        'xlsx',
        'mp3',
        'mp4',
        'm4a',
        'm4v'
    ];

    // Properties
    attachment: Attachment;
    errors: ErrorList[] = [];
    originalAttachment: Attachment;
    documentTypeId: string;
    documentTypes: Lookup[];
    V10: boolean;
    saved = false;
    newAttachment = false;
    editAttachment = false;
    showPortalVisibility = false;
    forGradebook: boolean;
    type = '';
    @Output() saveEvent = new EventEmitter();
    requireDescription: boolean;
    requireDocumentType: boolean;
    @ViewChild('form', { read: NgForm, static: true }) formRef: NgForm;
    @ViewChild('descriptionField', { read: ElementRef }) descriptionField: ElementRef;
    @ViewChild('documentTypeField', { read: ElementRef }) documentTypeField: ElementRef;
    allowedFileTypes: string[] = AddAttachmentDialogComponent.DefaultAllowedFileTypes;
    fileTypeErrorMessageText: string = 'Only .txt, .doc(x), .xls(x), .pdf, .jpg, .png, .gif, .mp3, m4a, .mp4, and m4v files can be uploaded.';

    // Class Functions
    constructor(
        @Inject(MAT_DIALOG_DATA) private data: any,
        private dialogBox: MatDialogRef<AddAttachmentDialogComponent>,
        private securityService: SecurityService,
        private loginService: LoginService
    ) { }

    // Event Functions
    ngOnInit() {
        // PRE: injected dialog data with attachment and
        // POST: initializes the data
        this.type = this.data.type;
        this.attachment = this.data.attachment;
        this.newAttachment = this.data.newAttachment;
        if (this.data.editAttachment != null && this.data.editAttachment != undefined) {
            this.editAttachment = this.data.editAttachment;
        }
        this.originalAttachment = new Attachment();
        Object.assign(this.originalAttachment, this.attachment);
        this.V10 = this.loginService.LoginSettings.V10;
        this.documentTypes = this.data.documentTypes;
        this.forGradebook = this.data.forGradebook;
        this.requireDescription = this.data.requireDescription;
        this.requireDocumentType = this.data.requireDocumentType;
        if (this.data.allowedFileTypes) {
            this.allowedFileTypes = this.data.allowedFileTypes;
        }
        if (this.data.fileTypeErrorMessageText) {
            this.fileTypeErrorMessageText = this.data.fileTypeErrorMessageText;
        }
        if (this.newAttachment == false || this.editAttachment == true) {
            const filExtension = this.attachment.FileName.split('.');
            this.attachment.FileExtension = filExtension[1];
            //  Needed because DocumentTypeId is a number, while LookUp id is a string
            if (this.attachment.DocumentTypeId != undefined) {
                this.documentTypeId = this.attachment.DocumentTypeId.toString();
            } else {
                this.documentTypeId = '0';
            }
        }

        if (!this.V10)
            this.getDocumentTypePermissions();
    }

    save() {
        // PRE: the save button is clicked
        // POST: if valid, closes the dialog with saved as true
        this.validate();
        if (this.errors.length == 0) {
            this.saved = true;
            //  Needed because DocumentTypeId is a number, while LookUp id is a string
            this.attachment.DocumentTypeId = parseInt(this.documentTypeId, 10);
            if (!this.V10 && this.attachment.DocumentTypeId > 0)
                this.attachment.DocumentType = this.documentTypes.filter(dt => dt.Id == this.documentTypeId)[0].CodeDescription;
            this.saveEvent.emit(this.attachment);
            this.dialogBox.close(true);
        } else {
            System.forceUpdateValidity(this.formRef);
        }
    }

    close() {
        // PRE: the close button is clicked
        // POST: resets the attachment and closes the dialog
        Object.assign(this.attachment, this.originalAttachment);
        this.dialogBox.close(false);
    }

    // Methods
    validate() {
        // POST: sets the errors or changes the data to conform with preconditions
        this.errors = [];

        if (this.attachment.FileName == '') {
            this.errors.push({
                fieldRef: null,
                fieldName: 'File',
                errorMsg: 'File required.'
            });
        }
        if (this.attachment.FileName.split('.').slice(0, -1).join('.').length > 100) {
            this.errors.push({
                fieldRef: null,
                fieldName: 'File',
                errorMsg: 'The file name you are attaching is greater than 100 characters. Please rename the file you are attaching to 100 characters or less.'
            });
        }
        if (this.requireDescription && !this.V10 && !this.attachment.Description) {
            this.errors.push({
                fieldRef: this.descriptionField,
                fieldName: 'Description',
                errorMsg: 'Required field.'
            });
        }
        else if (this.requireDescription && this.V10 && !this.attachment.FileDescription) {
            this.errors.push({
                fieldRef: this.descriptionField,
                fieldName: 'File Description',
                errorMsg: 'Required field.'
            });
        }

        else if (this.type == 'ImportedDocuments') {
            if (!this.attachment.Description) {
                this.errors.push({
                    fieldRef: this.descriptionField,
                    fieldName: 'File Description',
                    errorMsg: 'Required field.'
                });
            }
        }
        if (this.requireDocumentType && this.V10 && !this.documentTypeId) {
            this.errors.push({
                fieldRef: this.documentTypeField,
                fieldName: 'File Type',
                errorMsg: 'Required field.'
            });
        }

        // Validating file if no other errors are present
        if (this.errors.length == 0) {
            const fileExtensionRegExp = /\.[a-z0-9]*$/;
            if (!this.allowedFileTypes.includes(this.attachment.FileExtension.toLowerCase())) {
                this.errors.push({
                    fieldRef: null,
                    fieldName: 'File',
                    errorMsg: this.fileTypeErrorMessageText
                });
            } else if (fileExtensionRegExp.test(this.attachment.FileName)) {
                const fileExt = this.attachment.FileName.split('.');
                if (fileExt[fileExt.length - 1] != this.attachment.FileExtension) {
                    this.errors.push({
                        fieldRef: null,
                        fieldName: '',
                        errorMsg: 'The file name\'s extension does not match the uploaded file.'
                    });
                }
            } else {
                this.attachment.FileName += '.' + this.attachment.FileExtension;
            }
        }
    }

    getDocumentTypePermissions() {
        this.documentTypes = JSON.parse(JSON.stringify(this.documentTypes));
        let maintainAllDocumentTypes = this.securityService.IsAllPermissionCodeAssigned('DSF_MADFSF');
        let maintainDocumentTypeCategories = this.securityService.userPermissions.filter(p => p.PermissionCode == 'DSF_MDSF');
        if (!maintainAllDocumentTypes) {
            for (let i = this.documentTypes.length - 1; i >= 0; i--) {
                if (this.documentTypes[i].PermissionCategory.length > 0 && maintainDocumentTypeCategories.filter(c => c.PermissionCategory == this.documentTypes[i].PermissionCategory).length == 0)
                    this.documentTypes.splice(i, 1);
            }
        }
    }
}